import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { X } from 'lucide-react'
import { fetchCategories } from '../../api/categories'
import {
  DEFAULT_FILTER_SPEC,
  type FacetResponse,
  type FilterSpec,
} from './filters/types'
import {
  readAttributesFromParams,
  setAttributesOnParams,
  attributeParamPrefix,
} from './filters/urlAdapter'
import type { UrlStateAdapter } from './filters/urlAdapter'
import type { RenderContext } from './filters/context'
import { renderSection } from './filters/FilterSections'

export interface ProductFiltersProps {
  /**
   * Which sections to render and in what order. When omitted the
   * component uses `DEFAULT_FILTER_SPEC`. Build a per-category spec via
   * `specFromCategoryConfig(category.filter_config)`.
   */
  spec?: FilterSpec
  /**
   * Live facet counts from `/api/v1/products/facets` for the current
   * URL-encoded filter set. When absent the component still renders, but
   * all option counts and disabled-states are skipped.
   */
  facets?: FacetResponse
  /**
   * URL-state adapter. When omitted the component falls back to
   * `useSearchParams` from react-router-dom. Pass `useVikeUrlAdapter()`
   * on SSR surfaces to avoid full-page reloads on filter toggles.
   */
  adapter?: UrlStateAdapter
}

/**
 * Top-level entry point. Dispatches to a URL adapter then to the inner panel.
 */
export default function ProductFilters(props: ProductFiltersProps = {}) {
  if (props.adapter) {
    return <ProductFiltersInner {...props} adapter={props.adapter} />
  }
  return <ProductFiltersReactRouter {...props} />
}

function ProductFiltersReactRouter(props: ProductFiltersProps) {
  const [rrParams, setRrParams] = useSearchParams()
  const adapter: UrlStateAdapter = {
    searchParams: rrParams,
    setSearchParams: (next) => setRrParams(next),
  }
  return <ProductFiltersInner {...props} adapter={adapter} />
}

function ProductFiltersInner({
  spec = DEFAULT_FILTER_SPEC,
  facets,
  adapter,
}: ProductFiltersProps & { adapter: UrlStateAdapter }) {
  const searchParams = adapter.searchParams
  const setSearchParams = adapter.setSearchParams

  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: fetchCategories,
  })

  const category = searchParams.get('category') ?? ''
  const subcategory = searchParams.get('subcategory') ?? ''
  const series = searchParams.get('series') ?? ''
  const selectedLabels = searchParams.getAll('labels')
  const priceRange = searchParams.get('price_range') ?? ''
  const stockStatusCsv = searchParams.get('stock_status') ?? ''
  const selectedStockStatuses = stockStatusCsv ? stockStatusCsv.split(',').filter(Boolean) : []
  const deliverySpeed = searchParams.get('delivery_speed') ?? ''
  const brand = searchParams.get('brand') ?? ''
  const lifecyclePhases = searchParams.getAll('lifecycle_phase')
  const legacyRaw = searchParams.get('legacy') ?? ''
  // Normalise to the 3-state contract; anything unexpected reads as 'all'.
  const legacy = legacyRaw === 'hide' || legacyRaw === 'only' ? legacyRaw : 'all'
  // T0.3: "Toon uitgefaseerde producten" toggle.
  const includeDiscontinued = searchParams.get('includeDiscontinued') === 'true'

  // Per-category attribute filters — read whichever `attribute[<key>][]`
  // params are present in the URL. We don't constrain this to the spec's
  // declared attribute keys: keeping read symmetric with what was written
  // means the badge count + clearAll cover every selected attribute even
  // if the spec changes between renders.
  const attributes = readAttributesFromParams(searchParams)
  const attributeSelectionCount = Object.values(attributes).reduce(
    (sum, vs) => sum + vs.length,
    0,
  )

  const activeCount =
    (category ? 1 : 0) +
    (subcategory ? 1 : 0) +
    (series ? 1 : 0) +
    selectedLabels.length +
    (priceRange ? 1 : 0) +
    selectedStockStatuses.length +
    (deliverySpeed ? 1 : 0) +
    (brand ? 1 : 0) +
    lifecyclePhases.length +
    (legacy !== 'all' ? 1 : 0) +
    (includeDiscontinued ? 1 : 0) +
    attributeSelectionCount

  function setParam(key: string, value: string) {
    const next = new URLSearchParams(searchParams)
    if (value) next.set(key, value)
    else next.delete(key)
    if (key === 'category') {
      // Switching the main category resets every category-scoped filter
      // (WEB-399). Otherwise stale subcategory/series/brand/attribute params
      // carry over and the new category shows 0 products.
      for (const k of ['subcategory', 'series', 'brand', 'labels', 'lifecycle_phase', 'legacy', 'includeDiscontinued', 'stock_status', 'delivery_speed', 'price_range']) {
        next.delete(k)
      }
      setAttributesOnParams(next, {})
    }
    next.delete('page')
    setSearchParams(next)
  }

  function toggleMulti(key: string, value: string) {
    const next = new URLSearchParams(searchParams)
    const current = next.getAll(key)
    next.delete(key)
    const updated = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value]
    updated.forEach((v) => next.append(key, v))
    next.delete('page')
    setSearchParams(next)
  }

  function toggleCsv(key: string, value: string) {
    const next = new URLSearchParams(searchParams)
    const current = (next.get(key) ?? '').split(',').filter(Boolean)
    const updated = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value]
    if (updated.length > 0) next.set(key, updated.join(','))
    else next.delete(key)
    next.delete('page')
    setSearchParams(next)
  }

  function toggleAttributeValue(attributeKey: string, value: string) {
    const next = new URLSearchParams(searchParams)
    const current = next.getAll(attributeParamPrefix(attributeKey))
    const updated = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value]
    // Re-write just this attribute's bucket — leave every other attribute
    // param untouched. setAttributesOnParams() deletes only the key it
    // re-appends, so this is safe even when the spec changes between
    // toggles.
    const nextAttrs: Record<string, readonly string[]> = {
      ...readAttributesFromParams(next),
      [attributeKey]: updated,
    }
    if (updated.length === 0) delete nextAttrs[attributeKey]
    setAttributesOnParams(next, nextAttrs)
    next.delete('page')
    setSearchParams(next)
  }

  function clearAll() {
    const next = new URLSearchParams(searchParams)
    for (const key of [
      'category',
      'subcategory',
      'series',
      'labels',
      'price_range',
      'lifecycle_phase',
      'legacy',
      'includeDiscontinued',
      'stock_status',
      'delivery_speed',
      'brand',
      'page',
    ]) {
      next.delete(key)
    }
    // Strip every `attribute[<key>][]` param (Wis alle filters drops them too).
    setAttributesOnParams(next, {})
    setSearchParams(next)
  }

  const ctx: RenderContext = {
    categories,
    facets,
    category,
    subcategory,
    series,
    brand,
    priceRange,
    deliverySpeed,
    selectedLabels,
    selectedStockStatuses,
    lifecyclePhases,
    legacy,
    includeDiscontinued,
    attributes,
    setParam,
    toggleMulti,
    toggleCsv,
    toggleAttributeValue,
    searchParams,
    setSearchParams,
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm text-neutral-900">
          Filters
          {activeCount > 0 && (
            <span className="ml-2 inline-flex items-center justify-center h-5 min-w-5 px-1 rounded-full bg-primary text-white text-xs">
              {activeCount}
            </span>
          )}
        </h3>
        {activeCount > 0 && (
          <button
            onClick={clearAll}
            className="text-xs text-primary hover:underline flex items-center gap-1"
          >
            <X className="h-3 w-3" /> Wis alle filters
          </button>
        )}
      </div>

      {spec.filters.map((entry) => renderSection(entry, ctx))}

      {activeCount > 0 && (
        <button
          onClick={clearAll}
          className="mt-2 text-sm text-primary hover:underline flex items-center gap-1 self-start"
        >
          <X className="h-3.5 w-3.5" /> Wis alle filters
        </button>
      )}
    </div>
  )
}
